import { signedUploadDropAsset } from "@/lib/signed-actions";

// Compress an image file to WebP <= ~200KB, max dimension 1600px.
export async function compressImage(file: File, opts?: { maxDim?: number; targetBytes?: number }): Promise<Blob> {
  const maxDim = opts?.maxDim ?? 1600;
  const targetBytes = opts?.targetBytes ?? 200_000;

  const bitmap = await createImageBitmap(file);
  let { width, height } = bitmap;
  if (Math.max(width, height) > maxDim) {
    const scale = maxDim / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  let quality = 0.85;
  let blob: Blob = await new Promise((res) =>
    canvas.toBlob((b) => res(b!), "image/webp", quality),
  );
  while (blob.size > targetBytes && quality > 0.5) {
    quality -= 0.1;
    blob = await new Promise((res) =>
      canvas.toBlob((b) => res(b!), "image/webp", quality),
    );
  }
  return blob;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const s = String(reader.result ?? "");
      const idx = s.indexOf(",");
      resolve(idx >= 0 ? s.slice(idx + 1) : s);
    };
    reader.readAsDataURL(blob);
  });
}

// Uploads a file (compressed) to IPFS via Pinata (server-side).
// Returns the ipfs:// URI (short — safe to store on-chain / in DB) plus an
// HTTPS gateway URL for <img src=""> previews.
export async function uploadDropImage(
  file: File,
  folder: "logos" | "gallery",
  address: string,
): Promise<{ url: string; gateway_url: string; cid: string; size: number }> {
  const blob = await compressImage(file);
  if (blob.size > 500_000) throw new Error("Image too large after compression");
  const data_b64 = await blobToBase64(blob);
  const res = await signedUploadDropAsset(address, {
    folder,
    size: blob.size,
    content_type: "image/webp",
    data_b64,
  });
  return { url: res.url, gateway_url: res.gateway_url, cid: res.cid, size: res.size };
}

// Convert an ipfs:// URI to an HTTPS gateway URL for display. Pass through http(s) URLs untouched.
export function ipfsToHttp(uri: string | null | undefined): string {
  if (!uri) return "";
  if (uri.startsWith("ipfs://")) return `https://gateway.pinata.cloud/ipfs/${uri.slice(7)}`;
  return uri;
}
